import { Topic } from '../../interfaces'
import { AnalyticsEvent, ENVIAR_COMUNICADO } from '../events'
import { buildRecipientProperties } from './utils/recipient-properties'

export interface EnviarComunicadoProperties {
  topic_id: string
  topic_type: string
  // only present for topic types that let the sender pick an audience
  audience?: string
  recipient_count: number
  recipient_types: string
  is_stage_wide: boolean
  has_attachments: boolean
  attachment_count: number
  allow_messages: boolean
  title_length: number
  // characters of the body once the rich text markup is stripped
  content_length: number
  term_id: string
  stage_id: string
}

// the term/stage the topic was sent in; both are guaranteed by the time the
// editor allows a submit (`checkPermissions` requires a current term)
export interface EnviarComunicadoContext {
  termId: string
  stageId: string
}

const HTML_TAGS = /<[^>]*>/g
const SPACES = /\s+/g

/**
 * Builds the `enviar_comunicado` event from the topic the API just created, so
 * the payload describes what was really sent (the empty-recipients case becomes
 * a stage recipient before reaching this point, not after).
 */
export function buildEnviarComunicadoEvent (topic: Topic, context: EnviarComunicadoContext): AnalyticsEvent {
  const attachments = topic.attachments || []
  const audience = formatAudience(topic.audience)

  const properties: EnviarComunicadoProperties = {
    topic_id: topic._id,
    topic_type: topic.type,
    ...buildRecipientProperties(topic.recipients),
    has_attachments: attachments.length > 0,
    attachment_count: attachments.length,
    allow_messages: !!topic.allowMessages,
    title_length: (topic.title || '').length,
    content_length: countCharacters(topic.content),
    term_id: context.termId,
    stage_id: context.stageId
  }

  if (audience) {
    properties.audience = audience
  }

  return {
    name: ENVIAR_COMUNICADO,
    properties
  }
}

function formatAudience (audience?: { guardian?: boolean, student?: boolean }): string | undefined {
  if (!audience) {
    return
  }

  const roles: string[] = []
  if (audience.guardian) {
    roles.push('guardian')
  }
  if (audience.student) {
    roles.push('student')
  }

  return roles.length ? roles.join('+') : undefined
}

function countCharacters (content?: string): number {
  return (content || '')
    .replace(HTML_TAGS, ' ')
    .replace(SPACES, ' ')
    .trim()
    .length
}
